import { useEffect, useState } from "react";

/**
 * Profil d'interaction courant.
 *
 * ── Pourquoi ce hook ne s'appelle pas `useIsMobile` ──────────────────────────
 * La largeur de l'écran répond à une question de MISE EN PAGE : combien de
 * colonnes tiennent. Elle ne dit rien de la manière dont on désigne les choses,
 * qui est une question différente et souvent contradictoire :
 *
 *   • un iPad Pro en paysage est large ET tactile ;
 *   • une fenêtre de navigateur réduite sur un portable est étroite ET pilotée
 *     à la souris ;
 *   • un portable tactile a les deux à la fois, et peut changer d'avis entre
 *     deux gestes.
 *
 * Décider « pas de survol » sur un critère de largeur se trompe donc dans les
 * trois cas. Les médias `pointer` et `hover` répondent, eux, exactement à la
 * bonne question : quelle est la précision du pointeur principal, et sait-il
 * survoler sans cliquer.
 *
 * D'où la règle du design system :
 *
 *   largeur (`sm:`, `lg:`)  →  ce qu'on MONTRE   (colonnes, densité, ordre)
 *   pointeur (ce hook)      →  ce qu'on ACTIVE   (survol, glisser, sélection,
 *                                                 taille des cibles, popover
 *                                                 contre feuille du bas)
 */
export type InteractionMode = "pointer" | "touch";

export interface InteractionProfile {
  /** `touch` : pointeur grossier sans survol fiable. `pointer` : souris, trackpad, stylet. */
  mode: InteractionMode;
  /** Raccourci : `mode === "touch"`. */
  isTouch: boolean;
  /** Le survol est un signal exploitable (infobulle, actions révélées au survol). */
  canHover: boolean;
  /** Le pointeur est assez précis pour viser une cible de 24px (poignée, resize). */
  isFinePointer: boolean;
  /** L'appareil possède un écran tactile, même s'il a aussi une souris. */
  hasTouchScreen: boolean;
  /** Largeur < 640px : décision de MISE EN PAGE, pas d'interaction. */
  isNarrow: boolean;
}

/** `matchMedia` réactif, avec repli sûr côté serveur. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Décrit comment l'utilisateur désigne les choses, en ce moment.
 *
 * @example
 * const { isTouch, canHover } = useInteraction();
 *
 * // Les actions d'une ligne de tableau : révélées au survol sur portable,
 * // toujours visibles au doigt — un survol qui n'existe pas ne révèle rien.
 * <RowActions className={canHover ? "opacity-0 group-hover:opacity-100" : undefined} />
 *
 * // Le glisser-déposer demande une visée fine : au doigt on propose des
 * // boutons « monter / descendre » à la place, jamais une poignée de 8px.
 * {isTouch ? <ReorderButtons /> : <DragHandle />}
 */
export function useInteraction(): InteractionProfile {
  // `any-pointer: fine` plutôt que `pointer: fine` pour détecter la PRÉSENCE
  // d'une souris : un portable tactile déclare souvent le doigt comme pointeur
  // principal alors qu'un trackpad est branché.
  const canHover = useMediaQuery("(hover: hover)");
  const isFinePointer = useMediaQuery("(any-pointer: fine)");
  const hasTouchScreen = useMediaQuery("(any-pointer: coarse)");
  const isNarrow = useMediaQuery("(max-width: 639px)");

  // Le mode tactile n'est retenu que si les deux signaux concordent : pointeur
  // grossier ET absence de survol. Un portable tactile garde donc le
  // comportement « pointeur », qui est le bon — il a un trackpad.
  const mode: InteractionMode = !canHover && hasTouchScreen ? "touch" : "pointer";

  return {
    mode,
    isTouch: mode === "touch",
    canHover,
    isFinePointer,
    hasTouchScreen,
    isNarrow,
  };
}
